import React, { useState, useEffect } from 'react';
import { QrCode, Search, Loader2, CheckCircle, Clock, Users, Lock, XCircle, Phone, Tag, Calendar, Hash } from 'lucide-react';
import { beneficiariesAPI, redemptionsAPI, vendorAPI, smsAPI } from '../services/api';

const VendorPortal = ({ vendorId }) => {
  const [searchInput, setSearchInput] = useState('');
  const [beneficiary, setBeneficiary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [vendorStats, setVendorStats] = useState({
    totalBeneficiaries: 0,
    pendingVouchers: 0,
    usedVouchers: 0,
    totalValue: 0,
  });

  useEffect(() => {
    fetchVendorStats();
  }, [vendorId]);

  const fetchVendorStats = async () => {
    try {
      const response = await vendorAPI.getStats(vendorId);
      setVendorStats(response.data);
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
    }
  };

  const handleSearchBeneficiary = async () => {
    setMessage('');
    setBeneficiary(null);
    
    let input = searchInput.trim();
    
    // Only convert to uppercase if it's not a UUID (UUIDs should remain as-is)
    // UUID format: 8-4-4-4-12 characters with dashes
    const isUUID = input.length === 36 && input.includes('-');
    if (!isUUID) {
      input = input.toUpperCase();
    }

    if (!input) {
      setMessage('يرجى إدخال الاسم، رقم الهاتف، أو رقم الهوية الوطنية.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await beneficiariesAPI.search(input);
      const beneficiaryData = response.data;
      
      // Parse assigned_vendors if it's a string
      if (beneficiaryData.assigned_vendors) {
        if (typeof beneficiaryData.assigned_vendors === 'string') {
          try {
            beneficiaryData.assigned_vendors = JSON.parse(beneficiaryData.assigned_vendors);
          } catch (e) {
            beneficiaryData.assigned_vendors = [beneficiaryData.assigned_vendors];
          }
        }
        if (!Array.isArray(beneficiaryData.assigned_vendors)) {
          beneficiaryData.assigned_vendors = [beneficiaryData.assigned_vendors];
        }
      } else {
        beneficiaryData.assigned_vendors = [];
      }
      
      setBeneficiary(beneficiaryData);

      const remaining = beneficiaryData.coupon_count - beneficiaryData.redeemed_count;

      if (remaining <= 0) {
        setMessage(`تم استرداد جميع القسائم ${beneficiaryData.coupon_count} للمستفيد ${beneficiaryData.name}.`);
      } else {
        setMessage(`تم العثور على المستفيد ${beneficiaryData.name}. جاهز لاسترداد إحدى القسائم ${remaining} المتبقية.`);
      }
      
    } catch (error) {
      console.error("Error searching beneficiary:", error);
      
      // Handle 404 specifically - show "No matching values" message
      if (error.response?.status === 404) {
        setMessage('No matching values');
        setBeneficiary(null);
      } else {
        // For other errors, show the error message but don't log out
        setMessage(error.response?.data?.error || 'حدث خطأ أثناء البحث.');
        setBeneficiary(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!beneficiary) return;
    
    const remaining = beneficiary.coupon_count - beneficiary.redeemed_count;

    if (remaining <= 0) {
      setMessage('الاسترداد مغلق: تم استرداد جميع القسائم المخصصة.');
      return;
    }

    setMessage('جاري معالجة الاسترداد...');
    
    try {
      const response = await redemptionsAPI.create({
        beneficiary_id: beneficiary.id,
        proof_of_transaction: `NATIONAL_ID_VERIFIED:${beneficiary.id_number}`
      });

      const uniqueCouponId = response.data.unique_coupon_id;

      // Update local state
      setBeneficiary(prev => ({ 
        ...prev, 
        redeemed_count: prev.redeemed_count + 1 
      }));

      // Update stats
      fetchVendorStats();

      const finalRemaining = beneficiary.coupon_count - (beneficiary.redeemed_count + 1);
      
      // Only send SMS when all vouchers are used
      if (finalRemaining === 0) {
        // Send SMS notification that all vouchers are used
        try {
          const response = await smsAPI.sendAllVouchersUsed(beneficiary.phone);
          
          if (response.data.success) {
            setMessage(`تم استرداد آخر قسيمة! تم إشعار المستفيد بأن جميع القسائم قد تم استخدامها. المعرف الفريد: ${uniqueCouponId}`);
          } else {
            setMessage(`تم استرداد آخر قسيمة! جميع القسائم مكتملة الآن. المعرف الفريد: ${uniqueCouponId}`);
            console.error('Failed to send SMS:', response.data.message);
          }
        } catch (error) {
          setMessage(`تم استرداد آخر قسيمة! جميع القسائم مكتملة الآن. المعرف الفريد: ${uniqueCouponId}`);
          console.error('SMS error:', error);
        }
      } else {
        setMessage(`تم استرداد القسيمة بنجاح! تبقى ${finalRemaining} قسيمة. المعرف الفريد: ${uniqueCouponId}`);
      }

    } catch (error) {
      console.error("Error redeeming coupon:", error);
      setMessage('فشل الاسترداد بسبب خطأ في قاعدة البيانات.');
    }
  };

  
  const totalRemaining = beneficiary ? beneficiary.coupon_count - beneficiary.redeemed_count : 0;
  const isRedemptionLocked = beneficiary && totalRemaining <= 0;

  return (
    <div className="p-4 md:p-8">
      <h2 className="text-3xl font-extrabold text-slate-900 mb-6 flex items-center">
        <QrCode className="w-8 h-8 mr-3 text-violet-600" /> بوابة استرداد البائعين
      </h2>
      
      {/* Vendor Dashboard */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border-l-4 border-violet-500">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-violet-500"/> نظرة عامة على عبء العمل المخصص لك
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-violet-50 p-4 rounded-lg border border-violet-200">
            <p className="text-sm font-medium text-gray-600 mb-2">عدد المستفيدين</p>
            <p className="text-3xl font-bold text-violet-800">{vendorStats.totalBeneficiaries || 0}</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <p className="text-sm font-medium text-gray-600 mb-2">القسائم المعلقة</p>
            <p className="text-3xl font-bold text-amber-800">{vendorStats.pendingVouchers || 0}</p>
          </div>
          <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
            <p className="text-sm font-medium text-gray-600 mb-2">القسائم المستخدمة</p>
            <p className="text-3xl font-bold text-emerald-800">{vendorStats.usedVouchers || 0}</p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <p className="text-sm font-medium text-gray-600 mb-2">القيمة الإجمالية</p>
            <p className="text-3xl font-bold text-indigo-800">
              {(vendorStats.totalValue || 0).toLocaleString('ar-SA')} <span className="text-lg">شيكل</span>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-xl mb-8">
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="البحث بالاسم، رقم الهاتف، أو رقم الهوية الوطنية"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-grow p-3 border-2 border-slate-300 rounded-lg focus:ring-violet-500 focus:border-violet-500 transition duration-150"
            onKeyPress={(e) => { if (e.key === 'Enter') handleSearchBeneficiary(); }}
          />
          <button
            onClick={handleSearchBeneficiary}
            disabled={isLoading || !searchInput}
            className="flex items-center bg-violet-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-violet-700 transition duration-300 shadow-md disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5 mr-2" />} بحث
          </button>
        </div>

        {message && (
          <div className={`mt-4 p-3 rounded-lg font-medium ${isRedemptionLocked ? 'bg-red-100 text-red-800' : 'bg-violet-100 text-violet-800'}`}>
            {message}
          </div>
        )}
      </div>

      {beneficiary && (
        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-violet-500">
          <h3 className="text-2xl font-bold text-violet-700 mb-4">تفاصيل المستفيد</h3>
          
          {/* Coupon Dashboard */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-300 shadow-md">
              <p className="text-sm font-medium text-emerald-600 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1"/> القسائم المستردة
              </p>
              <p className="text-4xl font-bold text-emerald-800 mt-1">{beneficiary.redeemed_count}</p>
              <p className="text-xs text-gray-500 mt-1">إجمالي المخصص: {beneficiary.coupon_count}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-300 shadow-md">
              <p className="text-sm font-medium text-amber-600 flex items-center">
                <Clock className="w-4 h-4 mr-1"/> القسائم المتبقية
              </p>
              <p className="text-4xl font-bold text-amber-800 mt-1">{totalRemaining}</p>
              <p className="text-xs text-gray-500 mt-1">جاهز للاسترداد الفوري</p>
            </div>
          </div>

          {/* Beneficiary Details - Name, National ID, Project Name, and Voucher Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-4 border-t border-gray-100">
            <div>
              <p className="text-sm text-gray-500 mb-1">اسم المستفيد</p>
              <p className="text-xl font-semibold text-gray-900">{beneficiary.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">رقم الهوية الوطنية</p>
              <p className="text-xl font-mono text-gray-900 bg-gray-100 p-2 rounded">{beneficiary.id_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">اسم المشروع</p>
              <p className="text-xl font-semibold text-indigo-600">
                {beneficiary.project_names || beneficiary.project_name || 'غير محدد'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">قيمة القسيمة</p>
              <p className="text-xl font-semibold text-green-600">
                {(beneficiary.coupon_value || 0).toLocaleString('ar-SA')} {beneficiary.coupon_value ? 'شيكل' : ''}
              </p>
            </div>
          </div>

          {/* Redemption Button Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            {isRedemptionLocked ? (
              <p className="text-red-500 text-lg font-semibold flex items-center">
                <XCircle className="w-6 h-6 mr-2" />
                الاسترداد مغلق. لا توجد قسائم متبقية لهذا المستفيد.
              </p>
            ) : (
              <button
                onClick={handleRedeemCoupon}
                disabled={isLoading}
                className="w-full flex items-center justify-center bg-green-600 text-white font-bold py-4 rounded-lg text-xl hover:bg-green-700 transition duration-300 shadow-xl"
              >
                <CheckCircle className="w-6 h-6 mr-2" />
                استرداد قسيمة واحدة
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorPortal;
